import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';

export type DragRangePreset =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'last2Weeks'
  | 'last7Days'
  | 'thisMonth'
  | 'lastMonth';

interface CalendarDay {
  date: Date;
  key: string;
  inMonth: boolean;
  isToday: boolean;
}

const DAY_MS = 86400000;

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const dayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daySpan = (a: Date, b: Date): number => Math.round(Math.abs(startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY_MS) + 1;

const PRESET_LABELS: Record<DragRangePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This week',
  lastWeek: 'Last week',
  last2Weeks: 'Fortnight',
  last7Days: 'Last 7 days',
  thisMonth: 'This month',
  lastMonth: 'Last month',
};

/**
 * Date range picker opened from a pill button. Drag across days (mouse or touch), click a start then an
 * end day, or use the arrow keys with Enter. Emits [start, end] in date order.
 */
@Component({
  selector: 'app-drag-range-calendar',
  templateUrl: './drag-range-calendar.component.html',
  styleUrls: ['./drag-range-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DragRangeCalendarComponent implements OnChanges {
  @Input() start: Date | null = null;
  @Input() end: Date | null = null;
  @Input() presets: DragRangePreset[] = ['thisWeek', 'last2Weeks', 'thisMonth'];
  /** Longest selectable range in days; longer drags are clipped at the limit. */
  @Input() maxDays: number | null = null;
  /** 0 = Sunday, 1 = Monday. */
  @Input() weekStartsOn = 1;
  @Input() disabled = false;
  @Input() placeholder = 'Select dates';
  @Output() rangeChange = new EventEmitter<[Date, Date]>();

  @ViewChild('op') overlay?: OverlayPanel;
  @ViewChild('grid') grid?: ElementRef<HTMLElement>;

  viewMonth = startOfDay(new Date());
  days: CalendarDay[] = [];
  weekdayLabels: string[] = [];
  focusKey = '';

  private anchor: Date | null = null;
  private hover: Date | null = null;
  private dragging = false;
  /** First click placed; waiting for the end day. */
  pendingEnd = false;

  readonly presetLabels = PRESET_LABELS;

  constructor(private cdr: ChangeDetectorRef) {
    this.buildMonth();
  }

  ngOnChanges(): void {
    if (this.start && !this.overlay?.overlayVisible) {
      this.viewMonth = new Date(this.start.getFullYear(), this.start.getMonth(), 1);
      this.buildMonth();
    }
  }

  get label(): string {
    if (!this.start || !this.end) return this.placeholder;
    return this.formatRange(this.start, this.end);
  }

  get monthLabel(): string {
    return this.viewMonth.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  }

  get previewLabel(): string {
    const [a, b] = this.displayRange();
    if (this.pendingEnd && a) return `${this.formatDay(a, true)} → pick an end day`;
    if (!a || !b) return 'Drag across days, or click a start and an end day';
    const n = daySpan(a, b);
    return `${this.formatRange(a, b)} · ${n} day${n === 1 ? '' : 's'}`;
  }

  toggle(event: Event): void {
    if (this.disabled) return;
    this.overlay?.toggle(event);
  }

  onShow(): void {
    const focus = this.end || this.start || new Date();
    this.viewMonth = new Date(focus.getFullYear(), focus.getMonth(), 1);
    this.focusKey = dayKey(focus);
    this.resetDraft();
    this.buildMonth();
    this.cdr.markForCheck();
    setTimeout(() => this.focusDayButton(), 0);
  }

  onHide(): void {
    this.resetDraft();
    this.cdr.markForCheck();
  }

  shiftMonth(delta: number): void {
    this.viewMonth = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth() + delta, 1);
    this.buildMonth();
  }

  applyPreset(preset: DragRangePreset): void {
    const [a, b] = this.presetRange(preset);
    this.anchor = a;
    this.commit(a, b);
  }

  isPresetActive(preset: DragRangePreset): boolean {
    if (!this.start || !this.end) return false;
    const [a, b] = this.presetRange(preset);
    return dayKey(this.start) === dayKey(a) && dayKey(this.end) === dayKey(b);
  }

  private presetRange(preset: DragRangePreset): [Date, Date] {
    const today = startOfDay(new Date());
    const weekStart = addDays(today, -((today.getDay() - this.weekStartsOn + 7) % 7));
    const y = today.getFullYear();
    const m = today.getMonth();
    switch (preset) {
      case 'today':
        return [today, today];
      case 'yesterday':
        return [addDays(today, -1), addDays(today, -1)];
      case 'thisWeek':
        return [weekStart, addDays(weekStart, 6)];
      case 'lastWeek':
        return [addDays(weekStart, -7), addDays(weekStart, -1)];
      case 'last2Weeks':
        return [addDays(weekStart, -7), addDays(weekStart, 6)];
      case 'last7Days':
        return [addDays(today, -6), today];
      case 'thisMonth':
        return [new Date(y, m, 1), new Date(y, m + 1, 0)];
      case 'lastMonth':
        return [new Date(y, m - 1, 1), new Date(y, m, 0)];
    }
  }

  // ── Pointer interaction ──

  onPointerDown(day: CalendarDay, event: PointerEvent): void {
    if (event.button !== 0) return;
    event.preventDefault();
    // Touch pointers are implicitly captured by the pressed element; release so moves report the day underneath.
    (event.target as Element).releasePointerCapture?.(event.pointerId);
    this.focusKey = day.key;
    if (this.pendingEnd && this.anchor) {
      this.commit(this.anchor, day.date);
      return;
    }
    this.anchor = day.date;
    this.hover = day.date;
    this.dragging = true;
    this.pendingEnd = false;
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.dragging && !this.pendingEnd) return;
    const el = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null;
    const key = el?.closest<HTMLElement>('[data-day]')?.dataset['day'];
    if (!key) return;
    const day = this.days.find(d => d.key === key);
    if (day && (!this.hover || dayKey(this.hover) !== key)) {
      this.hover = this.clampToMax(day.date);
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:pointerup')
  onPointerUp(): void {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.anchor && this.hover && dayKey(this.anchor) !== dayKey(this.hover)) {
      this.commit(this.anchor, this.hover);
    } else {
      this.pendingEnd = true;
    }
    this.cdr.markForCheck();
  }

  // ── Keyboard ──

  onKeydown(event: KeyboardEvent): void {
    const current = this.days.find(d => d.key === this.focusKey)?.date || this.start || new Date();
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (event.key in moves) {
      event.preventDefault();
      this.moveFocus(addDays(current, moves[event.key]));
      return;
    }
    if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      this.moveFocus(new Date(current.getFullYear(), current.getMonth() + (event.key === 'PageUp' ? -1 : 1), current.getDate()));
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.pendingEnd && this.anchor) {
        this.commit(this.anchor, current);
      } else {
        this.anchor = current;
        this.hover = current;
        this.pendingEnd = true;
        this.cdr.markForCheck();
      }
      return;
    }
    if (event.key === 'Escape') {
      if (this.pendingEnd) {
        event.stopPropagation();
        this.resetDraft();
        this.cdr.markForCheck();
      } else {
        this.overlay?.hide();
      }
    }
  }

  // ── Rendering helpers ──

  dayClasses(day: CalendarDay): Record<string, boolean> {
    const [a, b] = this.displayRange();
    const k = day.key;
    const startKey = a ? dayKey(a) : '';
    const endKey = b ? dayKey(b) : '';
    const inRange = !!a && !!b && k >= startKey && k <= endKey;
    return {
      'is-out': !day.inMonth,
      'is-today': day.isToday,
      'is-in': inRange,
      'is-start': k === startKey,
      'is-end': k === endKey,
      'is-draft': this.dragging || this.pendingEnd,
      'is-focus': k === this.focusKey,
    };
  }

  trackDay(_: number, day: CalendarDay): string {
    return day.key;
  }

  private displayRange(): [Date | null, Date | null] {
    if ((this.dragging || this.pendingEnd) && this.anchor) {
      const other = this.hover || this.anchor;
      return this.anchor <= other ? [this.anchor, other] : [other, this.anchor];
    }
    return [this.start, this.end];
  }

  private clampToMax(target: Date): Date {
    if (!this.maxDays || !this.anchor || daySpan(this.anchor, target) <= this.maxDays) return target;
    const dir = target >= this.anchor ? 1 : -1;
    return addDays(this.anchor, dir * (this.maxDays - 1));
  }

  private commit(a: Date, b: Date): void {
    this.anchor = a;
    const clamped = this.clampToMax(b);
    const [from, to] = a <= clamped ? [startOfDay(a), startOfDay(clamped)] : [startOfDay(clamped), startOfDay(a)];
    this.start = from;
    this.end = to;
    this.resetDraft();
    this.rangeChange.emit([from, to]);
    this.overlay?.hide();
    this.cdr.markForCheck();
  }

  private resetDraft(): void {
    this.anchor = null;
    this.hover = null;
    this.dragging = false;
    this.pendingEnd = false;
  }

  private moveFocus(date: Date): void {
    if (date.getMonth() !== this.viewMonth.getMonth() || date.getFullYear() !== this.viewMonth.getFullYear()) {
      this.viewMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      this.buildMonth();
    }
    this.focusKey = dayKey(date);
    if (this.pendingEnd) this.hover = this.clampToMax(date);
    this.cdr.markForCheck();
    setTimeout(() => this.focusDayButton(), 0);
  }

  private focusDayButton(): void {
    this.grid?.nativeElement.querySelector<HTMLElement>(`[data-day="${this.focusKey}"]`)?.focus();
  }

  private buildMonth(): void {
    const first = new Date(this.viewMonth.getFullYear(), this.viewMonth.getMonth(), 1);
    const lead = (first.getDay() - this.weekStartsOn + 7) % 7;
    const gridStart = addDays(first, -lead);
    const todayKey = dayKey(new Date());
    this.days = Array.from({ length: 42 }, (_, i) => {
      const date = addDays(gridStart, i);
      const key = dayKey(date);
      return { date, key, inMonth: date.getMonth() === first.getMonth(), isToday: key === todayKey };
    });
    this.weekdayLabels = Array.from({ length: 7 }, (_, i) =>
      addDays(gridStart, i).toLocaleDateString('en-AU', { weekday: 'short' }).slice(0, 2)
    );
    if (!this.days.some(d => d.key === this.focusKey)) {
      this.focusKey = dayKey(first);
    }
  }

  private formatDay(d: Date, withYear: boolean): string {
    return d.toLocaleDateString('en-AU', withYear ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' });
  }

  private formatRange(a: Date, b: Date): string {
    if (dayKey(a) === dayKey(b)) return this.formatDay(a, true);
    const sameYear = a.getFullYear() === b.getFullYear();
    return `${this.formatDay(a, !sameYear)} – ${this.formatDay(b, true)}`;
  }
}
